import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslate } from 'services';
import { useTheme } from 'hooks';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  z-index: 1000000;
  position: absolute;
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CenterContainer = styled.div<{ background: string, border: string }>`
  width: 70%;
  overflow-y: scroll;
  background: ${({ background }) => background};
  border-radius: 1rem;
  border: 2px solid ${({ border }) => border};
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ModalButton = styled.button<{ color?: string, background: string, border: string }>`
  flex-grow: 1;
  padding: 1rem;
  border: none;
  border-top: 2px solid ${({ border }) => border};
  background: ${({ background }) => background};
  font-weight: 600;
  font-size: 1rem;
  color: ${({ color }) => (color || '#000')};

  &+button {
    border-left: 2px solid ${({ border }) => border}
  }
`;

const CountdownModalButton = styled(ModalButton)<{ progress: number }>`
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => props.progress}%;
    background: rgba(255, 255, 255, 0.3) 100%;
    transition: width 0.1s ease-out;
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

type ModalAction = {
  click: Function;
  buttonTextTranslationKey: string;
  // When set, the button auto-triggers after this many seconds unless clicked first, filling with a countdown graphic.
  timeoutSeconds?: number;
};

export type ConfirmCancelModalPropTypes = {
  confirmTextTranslationKey: string;
  onConfirm: ModalAction | null;
  onCancel: ModalAction | null,
};

type ModalActionButtonProps = {
  action: ModalAction;
  color?: string;
  background: string;
  border: string;
};

const ModalActionButton: React.FC<ModalActionButtonProps> = ({ action, color, background, border }) => {
  const { translate } = useTranslate();
  const { click, buttonTextTranslationKey, timeoutSeconds } = action;

  const initialCountdown = (timeoutSeconds ?? 0) * 1000;
  const [countdown, setCountdown] = useState<number>(initialCountdown);
  const [userClicked, setUserClicked] = useState<boolean>(false);

  // Hooks must run unconditionally, so the timeout branching happens inside the effect/render instead.
  useEffect(() => {
    if (!timeoutSeconds) return undefined;

    if (countdown > 0 && !userClicked) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 100);
      }, 100);
      return () => clearTimeout(timer);
    }

    if (countdown <= 0 && !userClicked) {
      click();
    }

    return undefined;
  }, [countdown, userClicked, timeoutSeconds, click]);

  if (!timeoutSeconds) {
    return (
      <ModalButton color={color} background={background} onClick={() => click()} border={border}>
        {translate(buttonTextTranslationKey)}
      </ModalButton>
    );
  }

  const handleClick = () => {
    setUserClicked(true);
    click();
  };

  const progress = userClicked ? 0 : ((initialCountdown - countdown) / initialCountdown) * 100;

  return (
    <CountdownModalButton color={color} background={background} onClick={handleClick} border={border} progress={progress}>
      {translate(buttonTextTranslationKey)}
    </CountdownModalButton>
  );
};

export const ConfirmCancelModal: React.FC<ConfirmCancelModalPropTypes> = ({ confirmTextTranslationKey, onConfirm, onCancel }) => {
  const { styles } = useTheme();
  const { translate } = useTranslate();
  return (
    <Wrapper>
      <CenterContainer background={styles.colorBackgroundBase} border={styles.colorBackgroundRaised}>
        <h5 style={{ padding: '2rem', color: styles.colorHeading, textAlign: 'center' }}>{translate(confirmTextTranslationKey)}</h5>
        <ButtonContainer>
          {onConfirm && (
            <ModalActionButton
              action={onConfirm}
              color={styles.colorPrimaryButtonText}
              background={styles.colorPrimaryButtonBackground}
              border={styles.colorBackgroundRaised}
            />
          )}
          {onCancel && (
            <ModalActionButton
              action={onCancel}
              color={styles.colorSecondaryButtonText}
              background={styles.colorSecondaryButtonBackground}
              border={styles.colorBackgroundRaised}
            />
          )}
        </ButtonContainer>
      </CenterContainer>
    </Wrapper>
  );
};
